import React, { useMemo, useState } from "react";
import { Collapse, Button, Checkbox, Empty, Space, message, Card } from "antd";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { RoleInfo, Role } from "../../Enums/Role";
import { PageHeader } from "../../components";
import { useMergedAccessPolicies, useSyncAccessPolicies } from "../../Services";
import { accessPolicyQueryKeys } from "../../Services/access_policy_service";

const ALL_ROLES = Object.values(RoleInfo).map((r) => r.name);

const MODULE_LABELS: Record<string, string> = {
  pos: "POS / Sales",
  sales_order: "Sales Orders",
  sales_return: "Sales Returns",
  quotation: "Quotations",
  repair_jobs: "Repair Jobs",
  expenses: "Expenses",
  cheques: "Cheques",
  credits: "Credits",
  dashboard: "Dashboard",
  products: "Products & Inventory",
  customers: "Customers",
  suppliers: "Suppliers",
  purchases: "Purchasing",
  raw_purchases: "Raw Purchases",
  reports: "Reports",
  hr: "HR Management",
  raw_materials: "Raw Materials",
  admin: "Administration",
  integrations: "Integrations",
};

const formatActionName = (key: string): string =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

type PolicyEntry = {
  path: string;
  defaultRoles: string[];
  mergedRoles: string[];
};

export const AccessPoliciesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const mergedEntries = useMergedAccessPolicies();
  const { syncPolicies, isSaving } = useSyncAccessPolicies();

  const [dirtyEntries, setDirtyEntries] = useState<Record<string, string[]>>(
    {},
  );

  const handleRoleToggle = (path: string, role: string, checked: boolean) => {
    setDirtyEntries((prev) => {
      const current = prev[path] ?? [];
      const updated = checked
        ? [...current, role]
        : current.filter((r) => r !== role);
      return { ...prev, [path]: updated };
    });
  };

  const getEffectiveRoles = (entry: PolicyEntry): string[] => {
    const override = dirtyEntries[entry.path];
    if (override !== undefined) return override;
    return entry.mergedRoles;
  };

  const hasChanges = Object.keys(dirtyEntries).length > 0;

  const handleSave = async () => {
    const updated = mergedEntries.map((entry) => ({
      ...entry,
      mergedRoles: dirtyEntries[entry.path] ?? entry.mergedRoles,
    }));
    const success = await syncPolicies(updated);
    if (success) {
      message.success("Access policies updated successfully.");
      setDirtyEntries({});
    } else {
      message.error("Failed to update access policies.");
    }
  };

  const handleReset = () => setDirtyEntries({});

  const handleModuleReset = (module: string) => {
    const next = { ...dirtyEntries };
    for (const entry of mergedEntries) {
      if (entry.path.startsWith(module + ".")) {
        delete next[entry.path];
      }
    }
    setDirtyEntries(next);
  };

  const groupedByModule = useMemo(() => {
    const map = new Map<string, PolicyEntry[]>();
    for (const entry of mergedEntries) {
      const module = entry.path.split(".")[0];
      if (!map.has(module)) map.set(module, []);
      map.get(module)!.push(entry);
    }
    return map;
  }, [mergedEntries]);

  const moduleKeys = useMemo(
    () => [...groupedByModule.keys()].sort(),
    [groupedByModule],
  );

  const collapseItems = useMemo(
    () =>
      moduleKeys.map((module) => {
        const entries = groupedByModule.get(module)!;
        const moduleDirtyCount = entries.filter(
          (e) => dirtyEntries[e.path] !== undefined,
        ).length;

        return {
          key: module,
          label: (
            <Space>
              <span>{MODULE_LABELS[module] ?? module}</span>
              <span className="text-gray-400 text-xs">
                {entries.length} {entries.length === 1 ? "policy" : "policies"}
                {moduleDirtyCount > 0 && ` \u00B7 ${moduleDirtyCount} modified`}
              </span>
            </Space>
          ),
          children: (
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase w-1/3">
                    Action
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">
                    Allowed Roles
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const effectiveRoles = getEffectiveRoles(entry);
                  const isModified = dirtyEntries[entry.path] !== undefined;
                  return (
                    <tr
                      key={entry.path}
                      className={`border-b border-gray-100 ${isModified ? "bg-yellow-50" : ""}`}
                    >
                      <td className="py-2.5 px-3 align-top">
                        <div className="text-sm font-medium">
                          {formatActionName(
                            entry.path.split(".").slice(1).join("."),
                          )}
                        </div>
                        <div className="text-xs text-gray-900 font-mono">
                          {entry.path}
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <Checkbox.Group
                          options={ALL_ROLES}
                          value={effectiveRoles}
                          onChange={(checkedValues) => {
                            setDirtyEntries((prev) => ({
                              ...prev,
                              [entry.path]: checkedValues as string[],
                            }));
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ),
          extra:
            moduleDirtyCount > 0 ? (
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleModuleReset(module);
                }}
              >
                Reset Module
              </Button>
            ) : undefined,
        };
      }),
    [moduleKeys, groupedByModule, dirtyEntries],
  );

  return (
    <div className="p-6">
      <PageHeader
        title="Access Policies"
        subText="Configure which roles can perform each action. Changes override application defaults."
        handleButtons={[
          {
            title: "Reset All",
            icon: ReloadOutlined,
            onClick: handleReset,
          },
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: () =>
              queryClient.invalidateQueries({
                queryKey: accessPolicyQueryKeys.overrides(),
              }),
          },
        ]}
        searchBar={
          <Space>
            <Button
              type="default"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </Space>
        }
      />

      <div className="mt-4">
        {moduleKeys.length === 0 ? (
          <Card>
            <Empty description="No access policies configured." />
          </Card>
        ) : (
          <Collapse
            items={collapseItems}
            expandIconPosition="end"
            defaultActiveKey={moduleKeys[0]}
            size="small"
          />
        )}
      </div>
    </div>
  );
};
